import { ArrowUpRightIcon } from 'lucide-react'

import { isCurrentVisionPlanOffThatch } from 'components/member_onboarding_v2/utils'
import { Banner, TextButton } from 'slab'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRestrictions,
} from 'utils/web_api.schemas'

import { PartialCoverageWarningBanner } from '../PartialCoverageWarningBanner/PartialCoverageWarningBanner'
import RenewalsPlanComparisonHeader from '../RenewalsPlanComparisonHeader/RenewalsPlanComparisonHeader'

export interface QLEVisionPlanCardProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  restrictions?: MemberOnboardingRestrictions;
}

export const QLEVisionPlanCard = ({
  planChangeSummary,
  restrictions = {},
}: QLEVisionPlanCardProps) => {
  const currentYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const currentVisionPlan = currentYear.vision_plan
  const nextYearVisionPlan = nextYear.vision_plan

  const offThatchVision = isCurrentVisionPlanOffThatch(planChangeSummary)

  // Use current year plan if vision plan is off-thatch, otherwise use next year plan
  const planToUse = offThatchVision ? currentVisionPlan : nextYearVisionPlan

  if (!planToUse) {
    return null
  }

  // Get the values to display
  const monthlyPremium = planToUse.premium_amount.formatted_amount
  const eyeExamCopay = planToUse.eye_exam_copay
  const framesOrContactsAllowance =
    planToUse.frames_or_contacts_allowance

  const planCannotBeChanged =
    restrictions?.vision_not_available === 'coverage_ends_mid_year'

  // Don't show "View full plan details" link for off-thatch plans
  const viewFullPlanDetailsUrl = !planCannotBeChanged && !offThatchVision && planToUse.sbc_url
    ? planToUse.sbc_url
    : undefined

  return (
    <div className='flex flex-col gap-6 border border-gray-200 rounded-2xl shadow-sm p-4 xs:p-6'>
      <RenewalsPlanComparisonHeader
        plan={planToUse}
        planType='vision'
      />
      {offThatchVision && (
        <Banner
          title='You purchased this plan outside of Thatch.'
          type='notice'
        />
      )}
      <PartialCoverageWarningBanner
        planChangeSummary={planChangeSummary}
        coverageType='vision'
      />
      <div className={`grid gap-4 ${offThatchVision ? 'grid-cols-1' : 'grid-cols-3'}`}>
        <div className='flex flex-col'>
          <div className='body-sm text-gray-500 mb-2'>Monthly premium</div>
          <div className='body text-gray-800'>{monthlyPremium}</div>
        </div>
        {!offThatchVision && (
          <>
            <div className='flex flex-col'>
              <div className='body-sm text-gray-500 mb-2'>Eye exam copay</div>
              <div className='body text-gray-800'>{eyeExamCopay}</div>
            </div>
            <div className='flex flex-col'>
              <div className='body-sm text-gray-500 mb-2'>
                Frames or contacts allowance
              </div>
              <div className='body text-gray-800'>
                {framesOrContactsAllowance}
              </div>
            </div>
          </>
        )}
      </div>
      {viewFullPlanDetailsUrl && (
        <TextButton
          onClick={() => {
            window.open(viewFullPlanDetailsUrl, '_blank')
          }}
          type='button'
          rightIcon={ArrowUpRightIcon}
        >
          View full plan details
        </TextButton>
      )}
    </div>
  )
}
